import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'horizontal' | 'featured';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="block chigua-card group cursor-pointer no-underline"
      >
        <div className="relative aspect-[16/9] overflow-hidden">
          <img
            src={article.coverImage}
            alt={article.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            title={`${article.title} - 吃瓜黑料`}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 p-5">
            <div className="flex items-center gap-2 mb-2">
              {article.isNew && (
                <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-accent-500 text-background-50 whitespace-nowrap">
                  最新
                </span>
              )}
              {article.isHot && (
                <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-primary-500 text-background-50 whitespace-nowrap">
                  热搜
                </span>
              )}
              <span className="px-2 py-0.5 text-xs rounded-full bg-background-50/20 text-background-50 whitespace-nowrap backdrop-blur-sm">
                {article.categoryName}
              </span>
            </div>
            <h3 className="text-lg md:text-xl font-heading font-bold text-white leading-snug line-clamp-2">
              {article.title}
            </h3>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'horizontal') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="flex gap-4 chigua-card p-3 group cursor-pointer no-underline"
      >
        <div className="w-28 h-20 flex-shrink-0 rounded-md overflow-hidden">
          <img
            src={article.coverImage}
            alt={article.title}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            title={`${article.title} - 吃瓜黑料`}
          />
        </div>
        <div className="flex-1 min-w-0 flex flex-col justify-between">
          <div>
            <h3 className="text-sm font-heading font-bold text-foreground-900 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors">
              {article.title}
            </h3>
            <p className="text-xs text-foreground-500 mt-1 line-clamp-2">
              {article.excerpt}
            </p>
          </div>
          <div className="flex items-center gap-2 text-xs text-foreground-400 mt-1">
            <span>{article.publishedAt}</span>
            <span>·</span>
            <span>{article.categoryName}</span>
            <span>·</span>
            <span className="flex items-center gap-0.5">
              <i className="ri-eye-line"></i>
              {article.views > 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.id}`}
      className="block chigua-card group cursor-pointer no-underline"
    >
      <div className="relative aspect-[16/10] overflow-hidden">
        <img
          src={article.coverImage}
          alt={article.title}
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          title={`${article.title} - 吃瓜黑料`}
        />
        <div className="absolute top-3 left-3 flex items-center gap-1.5">
          {article.isNew && (
            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-accent-500 text-background-50 whitespace-nowrap">
              最新
            </span>
          )}
          {article.isHot && (
            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-primary-500 text-background-50 whitespace-nowrap">
              热搜
            </span>
          )}
        </div>
        <span className="absolute top-3 right-3 px-2 py-0.5 text-xs rounded-full bg-background-950/60 text-background-50 whitespace-nowrap backdrop-blur-sm">
          {article.categoryName}
        </span>
      </div>
      <div className="p-4">
        <h3 className="text-sm font-heading font-bold text-foreground-900 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors">
          {article.title}
        </h3>
        <p className="text-xs text-foreground-500 mt-2 line-clamp-2 leading-relaxed">
          {article.excerpt}
        </p>
        <div className="flex items-center gap-2 text-xs text-foreground-400 mt-3 pt-3 border-t border-background-100">
          <span>{article.publishedAt}</span>
          <span className="flex items-center gap-0.5">
            <i className="ri-eye-line"></i>
            {article.views > 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views}
          </span>
        </div>
      </div>
    </Link>
  );
}